import * as THREE from 'three'
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls'
import Player, { Mode, GameMode } from '../player'
import Terrain, { BlockType, isFluidType } from '../terrain'
import Core from '../core'

import Block from '../terrain/mesh/block'
import Noise from '../terrain/noise'
import Audio from '../audio'
import BlockDropManager from '../drops'
import MobManager from '../mobs'
import { Mob } from '../mobs/Mob'
import { isMobile } from '../utils'
import {
  getFoodValue,
  InventoryItemType,
  isBlockType,
  isBucket,
  isFoodItem,
  isPickaxe,
  ItemType
} from '../items'
import type ExplosionManager from '../explosions'
enum Side {
  front,
  back,
  left,
  right,
  down,
  up
}

export default class Control {
  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    player: Player,
    terrain: Terrain,
    audio: Audio,
    core: Core
  ) {
    this.scene = scene
    this.camera = camera
    this.player = player
    this.terrain = terrain
    this.control = new PointerLockControls(camera, document.body)
    this.audio = audio
    this.core = core

    this.raycaster = new THREE.Raycaster()
    this.raycaster.far = 8
    this.far = this.player.body.height

    // collision sim mesh reuses one buffer for its whole lifetime — only the
    // first `count` instances are ever raycast
    this.tempMesh.instanceMatrix = this.tempMeshMatrix

    this.initRayCaster()
    this.initEventListeners()
  }

  // core properties
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  player: Player
  terrain: Terrain
  control: PointerLockControls
  audio: Audio
  core: Core
  velocity = new THREE.Vector3(0, 0, 0)
  portalTimer = 0
  portalCooldown = 0

  // collide and jump properties
  frontCollide = false
  backCollide = false
  leftCollide = false
  rightCollide = false
  downCollide = true
  upCollide = false
  isJumping = false

  raycasterDown = new THREE.Raycaster()
  raycasterUp = new THREE.Raycaster()
  raycasterFront = new THREE.Raycaster()
  raycasterBack = new THREE.Raycaster()
  raycasterRight = new THREE.Raycaster()
  raycasterLeft = new THREE.Raycaster()

  tempMesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial(),
    100
  )
  tempMeshMatrix = new THREE.InstancedBufferAttribute(
    new Float32Array(100 * 16),
    16
  )
  collideMatrixScratch = new THREE.Matrix4()

  // other properties
  p1 = performance.now()
  p2 = performance.now()
  raycaster: THREE.Raycaster
  far: number

  holdingBlock = BlockType.grass
  holdingItems: InventoryItemType[] = [
    BlockType.grass,
    BlockType.dirt,
    BlockType.stone,
    BlockType.sand,
    BlockType.tree,
    BlockType.wood,
    BlockType.craftingTable,
    BlockType.glass,
    BlockType.water
  ]
  holdingIndex = 0
  wheelGap = false
  clickInterval?: ReturnType<typeof setInterval>
  jumpInterval?: ReturnType<typeof setInterval>
  mouseHolding = false
  onDamage: (() => void) | null = null
  onOpenCraftingTable: (() => void) | null = null
  onMiningProgress: ((progress: number) => void) | null = null
  dropManager: BlockDropManager | null = null
  mobManager: MobManager | null = null
  active = false
  controlsBound = false
  miningTargetKey = ''
  miningStartedAt = 0
  miningDuration = 0
  swimming = false
  underwater = false
  inLava = false
  drownTimer = 0
  lavaTimer = 0
  fireTimer = 0
  explosions: ExplosionManager | null = null
  impulseForwardScratch = new THREE.Vector3()
  impulseRightScratch = new THREE.Vector3()
  homingTarget: Mob | null = null
  homingAttackTimer = 0
  homingAttackCooldown = 0
  homingAttackReady = true
  homingAttackDuration = 0.45
  homingAttackSpeed = 34
  homingAttackRange = 16
  homingAttackDamage = 8
  homingAttackHitRange = 1.45

  setActive(active: boolean) {
    this.active = active
    if (active) {
      this.bindGameplayEvents()
      this.p2 = performance.now()
      return
    }

    if (!active) {
      this.unbindGameplayEvents()
      this.clickInterval && clearInterval(this.clickInterval)
      this.jumpInterval && clearInterval(this.jumpInterval)
      this.resetMiningProgress()
      this.velocity.set(0, 0, 0)
      this.mouseHolding = false
      this.spaceHolding = false
      this.ctrlHolding = false
      this.isJumping = false
      this.homingTarget = null
      this.homingAttackTimer = 0
      this.homingAttackCooldown = 0
      this.homingAttackReady = true
      this.downKeys = {
        a: false,
        d: false,
        w: false,
        s: false
      }
      this.player.isFalling = false
      this.underwater = false
      this.inLava = false
      this.drownTimer = 0
      this.lavaTimer = 0
      this.fireTimer = 0
      this.player.setFluidSlow(1)
      this.p2 = performance.now()
    }
  }

  setDropManager(dm: BlockDropManager) {
    this.dropManager = dm
  }

  setMobManager(mm: MobManager) {
    this.mobManager = mm
  }

  setExplosionManager(em: ExplosionManager) {
    this.explosions = em
  }

  isUnderwater() {
    return this.underwater
  }

  isInLava() {
    return this.inLava
  }

  // convert a world-space impulse (explosions) into the camera-relative
  // velocity the movement code integrates
  applyWorldImpulse(impulse: THREE.Vector3) {
    if (this.isFlightMode()) return
    const forward = this.impulseForwardScratch
      .set(0, 0, -1)
      .applyQuaternion(this.camera.quaternion)
    forward.y = 0
    forward.normalize()
    const right = this.impulseRightScratch
      .set(1, 0, 0)
      .applyQuaternion(this.camera.quaternion)
    right.y = 0
    right.normalize()

    this.velocity.x += impulse.dot(forward)
    this.velocity.z += impulse.dot(right)
    this.velocity.y += impulse.y
    if (impulse.y > 0.5) {
      this.downCollide = false
      this.isJumping = true
    }
  }

  getBlockTypeFromMesh(mesh: THREE.InstancedMesh): BlockType {
    return (BlockType as unknown as Record<string, BlockType>)[mesh.name]
  }

  resetMiningProgress(notify = true) {
    this.miningTargetKey = ''
    this.miningStartedAt = 0
    this.miningDuration = 0
    if (notify) this.onMiningProgress?.(0)
  }

  getMiningDuration(type: BlockType) {
    if (type === BlockType.bedrock) return Number.POSITIVE_INFINITY

    const baseDurations: Record<BlockType, number> = {
      [BlockType.grass]: 130,
      [BlockType.sand]: 130,
      [BlockType.tree]: 380,
      [BlockType.leaf]: 70,
      [BlockType.dirt]: 130,
      [BlockType.stone]: 480,
      [BlockType.coal]: 560,
      [BlockType.wood]: 270,
      [BlockType.diamond]: 720,
      [BlockType.quartz]: 480,
      [BlockType.glass]: 140,
      [BlockType.bedrock]: Number.POSITIVE_INFINITY,
      [BlockType.craftingTable]: 270,
      [BlockType.water]: 70,
      [BlockType.lava]: 70,
      [BlockType.obsidian]: 4200,
      [BlockType.cobblestone]: 480,
      [BlockType.tnt]: 120,
      [BlockType.whitespaceGrass]: 130,
      [BlockType.whitespaceDirt]: 130,
      [BlockType.whitespaceLog]: 380,
      [BlockType.whitespaceLeaf]: 70,
      [BlockType.netherrack]: 80,
      [BlockType.soulSand]: 130,
      [BlockType.glowstone]: 90,
      [BlockType.netherQuartz]: 480,
      [BlockType.netherPortal]: 100
    }

    const held = this.getHoldingItemType()
    const duration = baseDurations[type] * this.getPickaxeSpeedFactor(held, type)

    return duration * this.player.getMoraleMiningMultiplier()
  }

  // blocks a pickaxe speeds up (and which, in survival, only drop when one
  // is held — obsidian demands the stone tier)
  isPickaxeBlock(type: BlockType) {
    return (
      type === BlockType.stone ||
      type === BlockType.coal ||
      type === BlockType.cobblestone ||
      type === BlockType.diamond ||
      type === BlockType.quartz
    )
  }

  getPickaxeSpeedFactor(held: InventoryItemType | null, type: BlockType) {
    if (type === BlockType.obsidian) {
      return held === ItemType.stonePickaxe ? 0.3 : 1
    }
    if (!this.isPickaxeBlock(type)) return 1
    if (held === ItemType.stonePickaxe) return 0.38
    if (held === ItemType.woodenPickaxe) return 0.55
    return 1
  }

  pickaxeAppliesTo(held: InventoryItemType | null, type: BlockType) {
    return (
      isPickaxe(held) && this.getPickaxeSpeedFactor(held, type) < 1
    )
  }

  canFinishMining(type: BlockType, position: THREE.Vector3) {
    if (this.player.gameMode === GameMode.creative) return true

    const duration = this.getMiningDuration(type)
    if (!Number.isFinite(duration)) {
      this.resetMiningProgress()
      return false
    }

    const targetKey = `${type}:${position.x}:${position.y}:${position.z}`
    const now = performance.now()
    if (targetKey !== this.miningTargetKey) {
      this.miningTargetKey = targetKey
      this.miningStartedAt = now - Math.min(60, duration * 0.35)
      this.miningDuration = duration
      this.onMiningProgress?.(Math.min(0.35, 60 / duration))
      return false
    }

    const progress = Math.min(1, (now - this.miningStartedAt) / this.miningDuration)
    this.onMiningProgress?.(progress)
    return progress >= 1
  }

  /**
   * Get the block type currently held based on inventory hotbar slot.
   * In creative mode, falls back to the creative hotbar item list.
   */
  getHoldingItemType(): InventoryItemType | null {
    if (this.player.gameMode === GameMode.survival) {
      const slot = this.player.inventory.getSlot(this.holdingIndex)
      return slot ? slot.type : null
    }
    return this.holdingItems[this.holdingIndex] ?? BlockType.grass
  }

  getHoldingBlockType(): BlockType {
    const itemType = this.getHoldingItemType()
    return itemType !== null && isBlockType(itemType) ? itemType : BlockType.grass
  }

  handleBucketUse(
    held: ItemType,
    targetType: BlockType,
    position: THREE.Vector3,
    normal: THREE.Vector3,
    hitMesh: THREE.InstancedMesh,
    hitInstanceId: number | null
  ) {
    const survival = this.player.gameMode === GameMode.survival

    if (held === ItemType.bucket) {
      if (!isFluidType(targetType)) return
      const scooped = this.terrain.fluid.scoop(
        position.x,
        position.y,
        position.z
      )
      if (!scooped) return
      // natural lava is finite — clear the worker-rendered instance we hit
      if (!scooped.clearedInstance && hitInstanceId !== null) {
        hitMesh.setMatrixAt(
          hitInstanceId,
          new THREE.Matrix4().set(
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
          )
        )
        hitMesh.instanceMatrix.needsUpdate = true
      }
      if (survival) {
        this.swapHeldBucket(
          scooped.type === BlockType.water
            ? ItemType.waterBucket
            : ItemType.lavaBucket
        )
      }
      this.audio.playSound(scooped.type)
      return
    }

    // filled buckets pour a source block against the targeted face
    const fluidType =
      held === ItemType.waterBucket ? BlockType.water : BlockType.lava
    const placed = this.terrain.fluid.placeSource(
      fluidType,
      position.x + normal.x,
      position.y + normal.y,
      position.z + normal.z
    )
    if (placed) {
      if (survival) {
        this.swapHeldBucket(ItemType.bucket)
      }
      this.audio.playSound(fluidType)
    }
  }

  // exchange ONE bucket in the held slot for another kind, preserving any
  // remaining stack (filled buckets never stack onto the leftovers)
  swapHeldBucket(resultType: ItemType) {
    const slot = this.player.inventory.getSlot(this.holdingIndex)
    if (!slot) return
    if (slot.count > 1) {
      slot.count--
      this.player.inventory.addItem(resultType, 1)
    } else {
      this.player.inventory.slots[this.holdingIndex] = {
        type: resultType,
        count: 1
      }
    }
  }

  spaceHolding = false

  initRayCaster = () => {
    this.raycasterUp.ray.direction = new THREE.Vector3(0, 1, 0)
    this.raycasterDown.ray.direction = new THREE.Vector3(0, -1, 0)
    this.raycasterFront.ray.direction = new THREE.Vector3(1, 0, 0)
    this.raycasterBack.ray.direction = new THREE.Vector3(-1, 0, 0)
    this.raycasterLeft.ray.direction = new THREE.Vector3(0, 0, -1)
    this.raycasterRight.ray.direction = new THREE.Vector3(0, 0, 1)

    this.raycasterUp.far = 1.2
    this.raycasterDown.far = this.player.body.height
    this.raycasterFront.far = this.player.body.width
    this.raycasterBack.far = this.player.body.width
    this.raycasterLeft.far = this.player.body.width
    this.raycasterRight.far = this.player.body.width
  }

  downKeys = {
    a: false,
    d: false,
    w: false,
    s: false
  }
  ctrlHolding = false

  isMoving() {
    return this.downKeys.w || this.downKeys.a || this.downKeys.s || this.downKeys.d
  }

  isSprinting() {
    return this.player.mode === Mode.sprinting || this.player.mode === Mode.sprintFlying
  }

  isGroundMovementMode() {
    return this.player.mode === Mode.walking || this.player.mode === Mode.sprinting
  }

  isFlightMode() {
    return this.player.mode === Mode.flying || this.player.mode === Mode.sprintFlying
  }

  isSwimming() {
    return this.swimming
  }

  isMining() {
    return this.miningTargetKey !== ''
  }

  getTargetedMob() {
    if (!this.mobManager) return null

    const mobMeshes = this.mobManager.mobs
      .filter(m => m.isAlive && !m.isDying)
      .map(m => m.mesh)
    const mobHits = this.raycaster.intersectObjects(mobMeshes, true)
    if (!mobHits.length) return null

    return this.mobManager.getMobFromObject(mobHits[0].object)
  }

  getMobCenter(mob: Mob) {
    return mob.mesh.position.clone().add(new THREE.Vector3(0, 1.1, 0))
  }

  findHomingTarget() {
    if (!this.mobManager) return null

    const lookDir = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(this.camera.quaternion)
      .normalize()
    let bestMob: Mob | null = null
    let bestScore = Number.POSITIVE_INFINITY

    for (const mob of this.mobManager.mobs) {
      if (!mob.isHostile || !mob.isAlive || mob.isDying) continue

      const toMob = this.getMobCenter(mob).sub(this.camera.position)
      const distance = toMob.length()
      if (distance < 1.5 || distance > this.homingAttackRange) continue

      const direction = toMob.normalize()
      const dot = direction.dot(lookDir)
      if (dot < 0.12 && distance > 6) continue

      const score = distance - dot * 8
      if (score < bestScore) {
        bestScore = score
        bestMob = mob
      }
    }

    return bestMob
  }

  startHomingAttack() {
    if (this.homingAttackCooldown > 0 || this.homingTarget) return false

    const target = this.findHomingTarget()
    if (!target) return false

    this.homingTarget = target
    this.homingAttackTimer = this.homingAttackDuration
    this.homingAttackReady = false
    this.isJumping = true
    this.player.isFalling = false
    return true
  }

  updateHomingAttack(delta: number) {
    if (!this.homingTarget) return false

    if (
      !this.homingTarget.isAlive ||
      this.homingTarget.isDying ||
      this.homingAttackTimer <= 0
    ) {
      this.homingTarget = null
      this.homingAttackTimer = 0
      return false
    }

    this.homingAttackTimer -= delta
    const targetPoint = this.getMobCenter(this.homingTarget)
    const toTarget = targetPoint.sub(this.camera.position)
    const distance = toTarget.length()

    if (distance <= this.homingAttackHitRange) {
      const knockbackDir = new THREE.Vector3()
        .subVectors(this.homingTarget.mesh.position, this.camera.position)
      this.homingTarget.takeDamage(this.homingAttackDamage, knockbackDir)
      this.velocity.y = 6
      this.homingTarget = null
      this.homingAttackTimer = 0
      this.homingAttackCooldown = 0.18
      return false
    }

    const step = Math.min(distance, this.homingAttackSpeed * delta)
    this.camera.position.addScaledVector(toTarget.normalize(), step)
    this.velocity.y = 0
    this.downCollide = false
    this.player.isFalling = false
    return true
  }

  // top of the contiguous fluid column the player is in (block-top Y)
  getLocalFluidSurfaceY(x: number, startY: number, z: number) {
    let top = startY
    let guard = 0
    while (
      guard++ < 48 &&
      (this.terrain.isWaterAt(x, top + 1, z) ||
        this.terrain.isLavaAt(x, top + 1, z))
    ) {
      top++
    }
    return top + 0.45
  }

  // drowning, lava damage, and burning — ticked every frame from update()
  updateFluidDamage(
    delta: number,
    eyeInWater: boolean,
    inWater: boolean,
    inLava: boolean
  ) {
    const player = this.player
    if (player.gameMode !== GameMode.survival || !player.alive) {
      player.air = player.maxAir
      player.onFire = 0
      this.drownTimer = 0
      this.lavaTimer = 0
      this.fireTimer = 0
      return
    }

    // drowning: ~15s of air, then 1 heart per second
    if (eyeInWater) {
      player.air = Math.max(0, player.air - delta)
      if (player.air <= 0) {
        this.drownTimer += delta
        if (this.drownTimer >= 1) {
          this.drownTimer = 0
          player.takeDamage(2)
          this.onDamage?.()
        }
      }
    } else {
      player.air = Math.min(player.maxAir, player.air + delta * 3)
      this.drownTimer = 0
    }

    // lava burns on contact and ignites you
    if (inLava) {
      player.setOnFire(6)
      this.lavaTimer += delta
      if (this.lavaTimer >= 0.5) {
        this.lavaTimer = 0
        player.takeDamage(3)
        this.onDamage?.()
      }
    } else {
      this.lavaTimer = 0
    }

    // burning persists out of lava; water extinguishes it
    if (player.onFire > 0) {
      if (inWater) {
        player.extinguish()
        this.fireTimer = 0
      } else {
        player.onFire = Math.max(0, player.onFire - delta)
        if (!inLava) {
          this.fireTimer += delta
          if (this.fireTimer >= 1) {
            this.fireTimer = 0
            player.takeDamage(1)
            this.onDamage?.()
          }
        }
      }
    } else if (!inLava) {
      this.fireTimer = 0
    }
  }

  reapplyVelocityFromKeys() {
    const forward = Number(this.downKeys.w) - Number(this.downKeys.s)
    const strafe = Number(this.downKeys.d) - Number(this.downKeys.a)
    const length = Math.hypot(forward, strafe)

    if (length === 0) {
      this.velocity.x = 0
      this.velocity.z = 0
      return
    }

    this.velocity.x = (forward / length) * this.player.speed
    this.velocity.z = (strafe / length) * this.player.speed
  }

  updateSprintMode() {
    if (this.player.gameMode === GameMode.survival && !this.player.alive) return
    const wantsSprint = this.ctrlHolding && this.downKeys.w
    const isFlying = this.player.mode === Mode.flying || this.player.mode === Mode.sprintFlying
    const isSneaking = this.player.mode === Mode.sneaking

    if (isSneaking) {
      this.reapplyVelocityFromKeys()
      return
    }

    let nextMode = this.player.mode
    if (isFlying) {
      nextMode = wantsSprint ? Mode.sprintFlying : Mode.flying
    } else {
      nextMode = wantsSprint ? Mode.sprinting : Mode.walking
    }

    if (nextMode !== this.player.mode) {
      this.player.setMode(nextMode)
    }
    this.reapplyVelocityFromKeys()
  }
  setMovementHandler = (e: KeyboardEvent) => {
    if (!this.active) return

    if (e.repeat) {
      return
    }

    switch (e.key) {
      case 'q':
        // no flying in survival mode
        if (this.player.gameMode === GameMode.survival) break
        if (this.player.mode === Mode.walking) {
          this.player.setMode(Mode.flying)
        } else {
          this.player.setMode(Mode.walking)
        }
        this.velocity.y = 0
        this.velocity.x = 0
        this.velocity.z = 0
        break
      case 'w':
      case 'W':
        this.downKeys.w = true
        this.updateSprintMode()
        break
      case 'Control':
        this.ctrlHolding = true
        this.updateSprintMode()
        break
      case 's':
      case 'S':
        this.downKeys.s = true
        this.reapplyVelocityFromKeys()
        break
      case 'a':
      case 'A':
        this.downKeys.a = true
        this.reapplyVelocityFromKeys()
        break
      case 'd':
      case 'D':
        this.downKeys.d = true
        this.reapplyVelocityFromKeys()
        break
      case ' ':
        if (this.player.mode === Mode.sneaking && !this.isJumping) {
          return
        }
        if (this.isGroundMovementMode()) {
          // jump
          if (!this.isJumping && this.downCollide) {
            this.velocity.y = 8
            this.isJumping = true
            this.homingAttackReady = false
            this.downCollide = false
            this.far = 0
            setTimeout(() => {
              this.far = this.player.body.height
            }, 300)
            // jumping costs hunger in survival
            if (this.player.gameMode === GameMode.survival) {
              this.player.depleteHunger(0.1)
            }
          } else if (
            !this.downCollide &&
            !this.swimming &&
            this.homingAttackReady
          ) {
            this.startHomingAttack()
          }
        } else if (this.isFlightMode()) {
          this.velocity.y += this.player.speed
        }
        if (this.isGroundMovementMode() && !this.spaceHolding) {
          this.spaceHolding = true
          this.jumpInterval = setInterval(() => {
            this.setMovementHandler(e)
          }, 10)
        }
        break
      case 'Shift':
        if (this.isGroundMovementMode()) {
          if (!this.isJumping) {
            this.player.setMode(Mode.sneaking)
            this.reapplyVelocityFromKeys()
            this.camera.position.setY(this.camera.position.y - 0.2)
          }
        } else if (this.isFlightMode()) {
          this.velocity.y -= this.player.speed
        }
        break
      default:
        break
    }
  }

  resetMovementHandler = (e: KeyboardEvent) => {
    if (!this.active) return

    if (e.repeat) {
      return
    }

    switch (e.key) {
      case 'w':
      case 'W':
        this.downKeys.w = false
        this.updateSprintMode()
        break
      case 'Control':
        this.ctrlHolding = false
        this.updateSprintMode()
        break
      case 's':
      case 'S':
        this.downKeys.s = false
        this.reapplyVelocityFromKeys()
        break
      case 'a':
      case 'A':
        this.downKeys.a = false
        this.reapplyVelocityFromKeys()
        break
      case 'd':
      case 'D':
        this.downKeys.d = false
        this.reapplyVelocityFromKeys()
        break
      case ' ':
        if (this.player.mode === Mode.sneaking && !this.isJumping) {
          return
        }
        this.jumpInterval && clearInterval(this.jumpInterval)
        this.spaceHolding = false
        this.homingAttackReady = true
        if (this.isGroundMovementMode()) {
          return
        }
        this.velocity.y = 0
        break
      case 'Shift':
        if (this.player.mode === Mode.sneaking) {
          if (!this.isJumping) {
            this.player.setMode(Mode.walking)
            this.reapplyVelocityFromKeys()
            this.camera.position.setY(this.camera.position.y + 0.2)
          }
        }
        if (this.isGroundMovementMode()) {
          return
        }
        this.velocity.y = 0
        break
      default:
        break
    }
  }

  mousedownHandler = (e: MouseEvent) => {
    e.preventDefault()
    if (!this.active) return
    if (this.player.gameMode === GameMode.survival && !this.player.alive) return
    if (e.button !== 0) {
      this.resetMiningProgress()
    }
    // let p1 = performance.now()
    this.raycaster.setFromCamera({ x: 0, y: 0 }, this.camera)
    const block = this.raycaster.intersectObjects(this.terrain.blocks)[0]
    const matrix = new THREE.Matrix4()

    switch (e.button) {
      // left click to remove block
      case 0:
        {
          // First check if we hit a mob
          if (this.mobManager) {
            this.raycaster.setFromCamera({ x: 0, y: 0 }, this.camera)
            const hitMob = this.getTargetedMob()
            if (hitMob) {
              const knockbackDir = new THREE.Vector3()
                .subVectors(hitMob.mesh.position, this.camera.position)
              hitMob.takeDamage(3, knockbackDir)
              this.resetMiningProgress()
              break
            }
          }

          if (block && block.object instanceof THREE.InstancedMesh) {
            // calculate position
            block.object.getMatrixAt(block.instanceId!, matrix)
            const position = new THREE.Vector3().setFromMatrixPosition(matrix)

            // determine the block type being broken
            const brokenType = this.getBlockTypeFromMesh(block.object)
            const breakingFluid = isFluidType(brokenType)

            // don't remove bedrock
            if (brokenType === BlockType.bedrock) {
              this.resetMiningProgress()
              this.terrain.generateAdjacentBlocks(position)
              break
            }

            if (!this.canFinishMining(brokenType, position)) {
              break
            }
            this.onMiningProgress?.(1)
            this.resetMiningProgress(false)

            // fluids: engine-owned cells drain through the engine; natural
            // fluid (ocean surface / cave lava) leaves a tombstone so the
            // worker stops rendering it
            if (breakingFluid) {
              const fluidBlock = this.terrain.getCustomBlock(
                position.x,
                position.y,
                position.z
              )
              if (
                fluidBlock &&
                fluidBlock.placed &&
                isFluidType(fluidBlock.type)
              ) {
                this.terrain.fluid.removeFluid(
                  fluidBlock.type,
                  position.x,
                  position.y,
                  position.z
                )
              } else {
                block.object.setMatrixAt(
                  block.instanceId!,
                  new THREE.Matrix4().set(
                    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
                  )
                )
                block.object.instanceMatrix.needsUpdate = true
                if (fluidBlock) {
                  fluidBlock.type = brokenType
                  fluidBlock.placed = false
                } else {
                  this.terrain.recordCustomBlock(
                    new Block(
                      position.x,
                      position.y,
                      position.z,
                      brokenType,
                      false
                    )
                  )
                }
                this.terrain.fluid.notifyBlockChanged(
                  position.x,
                  position.y,
                  position.z
                )
              }
              this.audio.playSound(brokenType)
              break
            }

            // tool durability: damage pickaxe when used to mine a block it applies to
            if (this.player.gameMode === GameMode.survival) {
              const held = this.getHoldingItemType()
              if (this.pickaxeAppliesTo(held, brokenType)) {
                this.player.inventory.damageTool(this.holdingIndex, 1)
              }
            }

            // remove the block
            block.object.setMatrixAt(
              block.instanceId!,
              new THREE.Matrix4().set(
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0,
                0
              )
            )

            // block and sound effect
            this.audio.playSound(brokenType)

            // spawn block drop entity (pickaxe-gated blocks only drop with a
            // pickaxe; obsidian demands the stone tier; stone drops cobble)
            if (this.dropManager) {
              const heldForDrop = this.getHoldingItemType()
              const survival = this.player.gameMode === GameMode.survival
              let shouldDrop = true
              if (survival) {
                if (brokenType === BlockType.obsidian) {
                  shouldDrop = heldForDrop === ItemType.stonePickaxe
                } else if (this.isPickaxeBlock(brokenType)) {
                  shouldDrop = isPickaxe(heldForDrop)
                }
              }
              if (shouldDrop) {
                const dropType =
                  brokenType === BlockType.stone
                    ? BlockType.cobblestone
                    : brokenType
                this.dropManager.spawn(position, dropType)
              }
            }

            // breaking animation (shrinking block)
            const mesh = new THREE.Mesh(
              new THREE.BoxGeometry(1, 1, 1),
              this.terrain.materials.get(
                this.terrain.materialType[brokenType]
              )
            )
            mesh.position.set(position.x, position.y, position.z)
            this.scene.add(mesh)
            const time = performance.now()
            let raf = 0
            const animate = () => {
              if (performance.now() - time > 250) {
                this.scene.remove(mesh)
                // material is shared from terrain.materials — do NOT dispose it.
                // geometry is freshly created above and would leak otherwise.
                mesh.geometry.dispose()
                cancelAnimationFrame(raf)
                return
              }
              raf = requestAnimationFrame(animate)
              mesh.scale.multiplyScalar(0.85)
            }
            animate()

            // update
            block.object.instanceMatrix.needsUpdate = true

            const existingBlock = this.terrain.getCustomBlock(
              position.x,
              position.y,
              position.z
            )
            if (existingBlock) {
              existingBlock.type = brokenType
              existingBlock.placed = false
            } else {
              this.terrain.recordCustomBlock(
                new Block(
                  position.x,
                  position.y,
                  position.z,
                  brokenType,
                  false
                )
              )
            }

            if (brokenType === BlockType.obsidian || brokenType === BlockType.netherPortal) {
              this.terrain.checkAndDeactivatePortals(position.x, position.y, position.z)
            }

            // generate adjacent blocks
            this.terrain.generateAdjacentBlocks(position)
          } else {
            this.resetMiningProgress()
          }
        }
        break

      // right click to put block
      case 2:
        {
          if (this.mobManager) {
            this.raycaster.setFromCamera({ x: 0, y: 0 }, this.camera)
            const hitMob = this.getTargetedMob()
            if (hitMob) {
              if (!hitMob.isHostile) {
                hitMob.toggleFollowTarget(this.camera)
              }
              break
            }
          }

          if (block && block.object instanceof THREE.InstancedMesh) {
            // calculate normal and position
            const normal = block.face!.normal
            block.object.getMatrixAt(block.instanceId!, matrix)
            const position = new THREE.Vector3().setFromMatrixPosition(matrix)
            const targetType = this.getBlockTypeFromMesh(block.object)

            if (targetType === BlockType.craftingTable) {
              this.onOpenCraftingTable?.()
              return
            }

            const heldType = this.getHoldingItemType()

            // prime TNT with anything that isn't a placeable block
            // (empty hand, stick, tools, ...). The creative hotbar can only
            // hold blocks, so creative ignites with anything except TNT
            // itself (which stacks instead).
            const canIgnite =
              this.player.gameMode === GameMode.creative
                ? heldType !== BlockType.tnt
                : heldType === null || !isBlockType(heldType)
            if (
              targetType === BlockType.tnt &&
              canIgnite &&
              !isBucket(heldType) &&
              this.explosions
            ) {
              this.explosions.ignite(position)
              block.object.setMatrixAt(
                block.instanceId!,
                new THREE.Matrix4().set(
                  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
                )
              )
              block.object.instanceMatrix.needsUpdate = true
              return
            }

            // buckets scoop and pour fluid sources
            if (isBucket(heldType)) {
              this.handleBucketUse(
                heldType as ItemType,
                targetType,
                position,
                normal,
                block.object,
                block.instanceId ?? null
              )
              return
            }

            if (
              this.player.gameMode === GameMode.survival &&
              heldType !== null &&
              isFoodItem(heldType)
            ) {
              const foodValue = getFoodValue(heldType)
              if (this.player.eat(foodValue)) {
                this.player.restoreMorale(foodValue)
                this.player.inventory.removeItem(this.holdingIndex, 1)
              }
              return
            }

            if (heldType === null || !isBlockType(heldType)) return
            const placeType = heldType

            // fluid blocks place as source blocks through the engine
            if (isFluidType(placeType)) {
              if (this.player.gameMode === GameMode.survival) {
                const slot = this.player.inventory.getSlot(this.holdingIndex)
                if (!slot || slot.type !== placeType || slot.count <= 0) return
              }
              const placedFluid = this.terrain.fluid.placeSource(
                placeType,
                position.x + normal.x,
                position.y + normal.y,
                position.z + normal.z
              )
              if (placedFluid) {
                if (this.player.gameMode === GameMode.survival) {
                  this.player.inventory.removeItem(this.holdingIndex, 1)
                }
                this.audio.playSound(placeType)
              }
              return
            }

            // in survival, check if we have the block in inventory
            if (this.player.gameMode === GameMode.survival) {
              const slot = this.player.inventory.getSlot(this.holdingIndex)
              if (!slot || slot.type !== placeType || slot.count <= 0) return
            }

            if (
              this.terrain.getCount(placeType) >=
              this.terrain.blocks[placeType].instanceMatrix.count
            ) {
              return
            }

            // return when block overlaps with player
            if (
              position.x + normal.x === Math.round(this.camera.position.x) &&
              position.z + normal.z === Math.round(this.camera.position.z) &&
              (position.y + normal.y === Math.round(this.camera.position.y) ||
                position.y + normal.y ===
                  Math.round(this.camera.position.y - 1))
            ) {
              return
            }

            // consume from inventory in survival
            if (this.player.gameMode === GameMode.survival) {
              this.player.inventory.removeItem(this.holdingIndex, 1)
            }

            // placing a solid block displaces any fluid in the target cell
            const displaced = this.terrain.getCustomBlock(
              normal.x + position.x,
              normal.y + position.y,
              normal.z + position.z
            )
            if (displaced && displaced.placed && isFluidType(displaced.type)) {
              this.terrain.fluid.removeFluid(
                displaced.type,
                normal.x + position.x,
                normal.y + position.y,
                normal.z + position.z
              )
            }

            // put the block
            matrix.setPosition(
              normal.x + position.x,
              normal.y + position.y,
              normal.z + position.z
            )
            this.terrain.blocks[placeType].setMatrixAt(
              this.terrain.getCount(placeType),
              matrix
            )
            this.terrain.setCount(placeType)
            this.terrain.blocks[placeType].count =
              this.terrain.getCount(placeType)

            //sound effect
            this.audio.playSound(placeType)

            // update
            this.terrain.blocks[placeType].instanceMatrix.needsUpdate =
              true

            // add to custom blocks
            this.terrain.recordCustomBlock(
              new Block(
                normal.x + position.x,
                normal.y + position.y,
                normal.z + position.z,
                placeType,
                true
              )
            )

            if (placeType === BlockType.obsidian) {
              this.terrain.checkAndActivatePortal(
                normal.x + position.x,
                normal.y + position.y,
                normal.z + position.z
              )
            }

            // new solid can redirect nearby flows
            this.terrain.fluid.notifyBlockChanged(
              normal.x + position.x,
              normal.y + position.y,
              normal.z + position.z
            )
          } else {
            const heldType = this.getHoldingItemType()
            if (
              this.player.gameMode === GameMode.survival &&
              heldType !== null &&
              isFoodItem(heldType)
            ) {
              const foodValue = getFoodValue(heldType)
              if (this.player.eat(foodValue)) {
                this.player.restoreMorale(foodValue)
                this.player.inventory.removeItem(this.holdingIndex, 1)
              }
            }
          }
        }
        break
      default:
        break
    }

    if (!isMobile && !this.mouseHolding) {
      this.mouseHolding = true
      this.clickInterval = setInterval(() => {
        this.mousedownHandler(e)
      }, e.button === 0 ? 75 : 333)
    }

    // console.log(performance.now() - p1)
  }
  mouseupHandler = () => {
    this.clickInterval && clearInterval(this.clickInterval)
    this.mouseHolding = false
    this.resetMiningProgress()
  }

  changeHoldingBlockHandler = (e: KeyboardEvent) => {
    if (!this.active) return
    if (isNaN(parseInt(e.key)) || e.key === '0') {
      return
    }
    this.holdingIndex = parseInt(e.key) - 1

    this.holdingBlock = this.getHoldingBlockType()
  }

  wheelHandler = (e: WheelEvent) => {
    if (!this.active) return
    if (!this.wheelGap) {
      this.wheelGap = true
      setTimeout(() => {
        this.wheelGap = false
      }, 100)
      if (e.deltaY > 0) {
        this.holdingIndex++
        this.holdingIndex > 8 && (this.holdingIndex = 0)
      } else if (e.deltaY < 0) {
        this.holdingIndex--
        this.holdingIndex < 0 && (this.holdingIndex = 8)
      }
      this.holdingBlock = this.getHoldingBlockType()
    }
  }

  bindGameplayEvents = () => {
    if (this.controlsBound) return
    document.addEventListener('keydown', this.changeHoldingBlockHandler)
    document.addEventListener('wheel', this.wheelHandler)
    document.addEventListener('keydown', this.setMovementHandler)
    document.addEventListener('keyup', this.resetMovementHandler)
    document.addEventListener('mousedown', this.mousedownHandler)
    document.addEventListener('mouseup', this.mouseupHandler)
    this.controlsBound = true
  }

  unbindGameplayEvents = () => {
    if (!this.controlsBound) return
    document.removeEventListener('keydown', this.changeHoldingBlockHandler)
    document.removeEventListener('wheel', this.wheelHandler)
    document.removeEventListener('keydown', this.setMovementHandler)
    document.removeEventListener('keyup', this.resetMovementHandler)
    document.removeEventListener('mousedown', this.mousedownHandler)
    document.removeEventListener('mouseup', this.mouseupHandler)
    this.controlsBound = false
  }

  initEventListeners = () => {
    // Keep pointer-lock state in sync for look controls. Gameplay input is
    // bound by setActive so embedded browsers can still move without lock.
    document.addEventListener('pointerlockchange', () => {
      if (!document.pointerLockElement) {
        this.velocity = new THREE.Vector3(0, 0, 0)
      }
    })
  }

  // move along X with direction factor
  moveX(distance: number, delta: number) {
    this.camera.position.x +=
      distance * (this.player.speed / Math.PI) * 2 * delta
  }

  // move along Z with direction factor
  moveZ = (distance: number, delta: number) => {
    this.camera.position.z +=
      distance * (this.player.speed / Math.PI) * 2 * delta
  }

  // collide checking
  collideCheckAll = (
    position: THREE.Vector3,
    noise: Noise,
    far: number
  ) => {
    this.collideCheck(Side.down, position, noise, far)
    this.collideCheck(Side.front, position, noise)
    this.collideCheck(Side.back, position, noise)
    this.collideCheck(Side.left, position, noise)
    this.collideCheck(Side.right, position, noise)
    this.collideCheck(Side.up, position, noise)
  }

  collideCheck = (
    side: Side,
    position: THREE.Vector3,
    noise: Noise,
    far: number = this.player.body.width
  ) => {
    const matrix = this.collideMatrixScratch

    //reset simulation blocks (buffer is reused; count bounds the raycast)
    let index = 0
    const maxTempBlocks = this.tempMesh.instanceMatrix.count
    const addCollisionBlock = (bx: number, by: number, bz: number) => {
      if (index >= maxTempBlocks) return
      matrix.setPosition(bx, by, bz)
      this.tempMesh.setMatrixAt(index++, matrix)
    }

    // block to remove
    let removed = false
    let treeRemoved = new Array<boolean>(
      this.terrain.noise.treeHeight + 1
    ).fill(false)

    // get block position
    let x = Math.round(position.x)
    let z = Math.round(position.z)

    switch (side) {
      case Side.front:
        x++
        this.raycasterFront.ray.origin = position
        break
      case Side.back:
        x--
        this.raycasterBack.ray.origin = position
        break
      case Side.left:
        z--
        this.raycasterLeft.ray.origin = position
        break
      case Side.right:
        z++
        this.raycasterRight.ray.origin = position
        break
      case Side.down:
        this.raycasterDown.ray.origin = position
        this.raycasterDown.far = far
        break
      case Side.up:
        this.raycasterUp.ray.origin = new THREE.Vector3().copy(position)
        this.raycasterUp.ray.origin.y--
        break
    }

    const y = noise.getTerrainHeight(x, z)

    // Track which natural underground blocks the player has mined out so the
    // fallback collision column can go to bedrock without refilling old tunnels.
    const minNaturalY = 0
    const removedNatural: Record<number, boolean> = {}

    // check custom blocks near the tested column
    this.terrain.forEachCustomBlockInBounds(x, 0, z, x, 512, z, block => {
      if (block.placed) {
        // placed blocks (fluids are never solid)
        if (!isFluidType(block.type) && block.type !== BlockType.netherPortal) {
          addCollisionBlock(block.x, block.y, block.z)
        }
      } else if (block.y === y) {
        // removed surface block
        removed = true
      } else if (block.y >= minNaturalY && block.y < y) {
        // removed natural underground block
        removedNatural[block.y] = true
      } else {
        for (let i = 1; i <= this.terrain.noise.treeHeight; i++) {
          if (block.y === y + i) {
            treeRemoved[i] = true
          }
        }
      }
    })

    // update simulation blocks (ignore removed blocks)
    if (!removed) {
      addCollisionBlock(x, y, z)
    }

    // Add the natural underground column as fallback collision so the player
    // never falls through unloaded mountain interior or freshly dug terrain.
    // Collision rays reach at most ~3 blocks, so only the slice around the
    // player's Y matters — not the full column to bedrock.
    const columnMinY = Math.max(
      minNaturalY,
      Math.floor(position.y) - 8
    )
    for (let by = y - 1; by >= columnMinY; by--) {
      if (removedNatural[by]) continue
      if (by > 0 && noise.isCaveAt(x, by, z, y)) continue
      addCollisionBlock(x, by, z)
    }
    if (noise.shouldGrowTreeAt(x, z, y)) {
      for (let i = 1; i <= this.terrain.noise.treeHeight; i++) {
        if (!treeRemoved[i]) {
          addCollisionBlock(x, y + i, z)
        }
      }
    }

    // sneaking check
    if (
      this.player.mode === Mode.sneaking &&
      y < Math.floor(this.camera.position.y - 2) &&
      side !== Side.down &&
      side !== Side.up
    ) {
      addCollisionBlock(x, Math.floor(this.camera.position.y - 1), z)
    }
    this.tempMesh.count = index
    this.tempMesh.instanceMatrix.needsUpdate = true

    // update collide
    const origin = new THREE.Vector3(position.x, position.y - 1, position.z)
    switch (side) {
      case Side.front: {
        const c1 = this.raycasterFront.intersectObject(this.tempMesh).length
        this.raycasterFront.ray.origin = origin
        const c2 = this.raycasterFront.intersectObject(this.tempMesh).length
        c1 || c2 ? (this.frontCollide = true) : (this.frontCollide = false)

        break
      }
      case Side.back: {
        const c1 = this.raycasterBack.intersectObject(this.tempMesh).length
        this.raycasterBack.ray.origin = origin
        const c2 = this.raycasterBack.intersectObject(this.tempMesh).length
        c1 || c2 ? (this.backCollide = true) : (this.backCollide = false)
        break
      }
      case Side.left: {
        const c1 = this.raycasterLeft.intersectObject(this.tempMesh).length
        this.raycasterLeft.ray.origin = origin
        const c2 = this.raycasterLeft.intersectObject(this.tempMesh).length
        c1 || c2 ? (this.leftCollide = true) : (this.leftCollide = false)
        break
      }
      case Side.right: {
        const c1 = this.raycasterRight.intersectObject(this.tempMesh).length
        this.raycasterRight.ray.origin = origin
        const c2 = this.raycasterRight.intersectObject(this.tempMesh).length
        c1 || c2 ? (this.rightCollide = true) : (this.rightCollide = false)
        break
      }
      case Side.down: {
        const c1 = this.raycasterDown.intersectObject(this.tempMesh).length
        c1 ? (this.downCollide = true) : (this.downCollide = false)
        break
      }
      case Side.up: {
        const c1 = this.raycasterUp.intersectObject(this.tempMesh).length
        c1 ? (this.upCollide = true) : (this.upCollide = false)
        break
      }
    }
  }

  teleportDimension = () => {
    const currentDimension = this.terrain.dimension
    const newDimension = currentDimension === 'overworld' ? 'nether' : 'overworld'

    if (currentDimension === 'overworld') {
      this.terrain.overworldCustomBlocks = [...this.terrain.customBlocks]
      this.terrain.customBlocks = this.terrain.netherCustomBlocks
    } else {
      this.terrain.netherCustomBlocks = [...this.terrain.customBlocks]
      this.terrain.customBlocks = this.terrain.overworldCustomBlocks
    }

    if (this.mobManager) {
      if (currentDimension === 'overworld') {
        this.mobManager.overworldMobs = [...this.mobManager.mobs]
        for (const mob of this.mobManager.mobs) {
          this.scene.remove(mob.mesh)
        }
        this.mobManager.mobs = this.mobManager.netherMobs || []
        for (const mob of this.mobManager.mobs) {
          this.scene.add(mob.mesh)
        }
      } else {
        this.mobManager.netherMobs = [...this.mobManager.mobs]
        for (const mob of this.mobManager.mobs) {
          this.scene.remove(mob.mesh)
        }
        this.mobManager.mobs = this.mobManager.overworldMobs || []
        for (const mob of this.mobManager.mobs) {
          this.scene.add(mob.mesh)
        }
      }
    }

    this.terrain.dimension = newDimension
    this.core.applyDimensionEnvironment(newDimension)
    this.terrain.markCustomBlocksDirty()

    const px = this.camera.position.x
    const py = this.camera.position.y
    const pz = this.camera.position.z

    let tx = px
    let ty = py
    let tz = pz

    if (newDimension === 'nether') {
      tx = Math.round(px / 8)
      tz = Math.round(pz / 8)
      ty = 35
    } else {
      tx = Math.round(px * 8)
      tz = Math.round(pz * 8)
      const overworldHeight = this.terrain.noise.getTerrainHeight(tx, tz)
      ty = overworldHeight + 2
    }

    this.terrain.createDestinationPortal(tx, ty, tz)

    this.camera.position.set(tx, ty + 1.6, tz)
    this.velocity.set(0, 0, 0)
    this.player.isFalling = false

    this.terrain.initBlocks()
    // force: discard any in-flight regen from the dimension we just left so its
    // result can't land in the new dimension's freshly-reset buffers
    this.terrain.generate({ force: true })

    this.audio.playPortalSound()
  }

  update = (forcedDelta?: number) => {
    this.p1 = performance.now()
    if (!this.active) {
      this.p2 = this.p1
      return
    }

    // clamp so a backgrounded tab doesn't integrate one giant physics step
    const delta =
      forcedDelta !== undefined
        ? forcedDelta
        : Math.min(0.1, (this.p1 - this.p2) / 1000)
    this.homingAttackCooldown = Math.max(0, this.homingAttackCooldown - delta)
    if (this.updateHomingAttack(delta)) {
      this.p2 = this.p1
      return
    }

    // cell-based fluid detection: blocks are centered on integer coords
    const rx = Math.round(this.camera.position.x)
    const rz = Math.round(this.camera.position.z)
    const eyeY = Math.round(this.camera.position.y)
    const feetY = Math.round(this.camera.position.y - 1.2)
    const eyeInWater = this.terrain.isWaterAt(rx, eyeY, rz)
    const inWater = eyeInWater || this.terrain.isWaterAt(rx, feetY, rz)
    const eyeInLava = this.terrain.isLavaAt(rx, eyeY, rz)
    const inLava = eyeInLava || this.terrain.isLavaAt(rx, feetY, rz)
    const inFluid = inWater || inLava

    if (this.portalCooldown > 0) {
      this.portalCooldown -= delta
    }

    const standingInPortal = this.terrain.isPortalAt(rx, eyeY, rz) || this.terrain.isPortalAt(rx, feetY, rz)
    if (standingInPortal && this.portalCooldown <= 0) {
      this.portalTimer += delta
      const portalDuration = this.player.gameMode === GameMode.creative ? 0.5 : 5.0
      if (this.portalTimer >= portalDuration) {
        this.teleportDimension()
        this.portalTimer = 0
        this.portalCooldown = 4.0
      }
    } else {
      if (this.portalTimer > 0) {
        this.portalTimer = Math.max(0, this.portalTimer - delta * 2.0)
      }
    }

    this.swimming = inFluid && !this.isFlightMode()
    this.underwater = eyeInWater && !this.isFlightMode()
    this.inLava = inLava

    // wading slows you down — lava far more than water
    const fluidSlow = this.swimming ? (inLava ? 0.4 : 0.72) : 1
    if (fluidSlow !== this.player.fluidSlow) {
      this.player.setFluidSlow(fluidSlow)
      this.reapplyVelocityFromKeys()
    }

    if (
      // dev mode
      this.player.mode === Mode.flying
    ) {
      this.control.moveForward(this.velocity.x * delta)
      this.control.moveRight(this.velocity.z * delta)
      this.camera.position.y += this.velocity.y * delta
    } else {
      // normal mode
      this.collideCheckAll(
        this.camera.position,
        this.terrain.noise,
        this.far - this.velocity.y * delta
      )

      if (this.swimming) {
        this.player.isFalling = false
        this.isJumping = false
        this.far = this.player.body.height

        // lava is thick: slower rise, gentler sink, weaker buoyancy
        const surfaceY = this.getLocalFluidSurfaceY(rx, feetY, rz)
        const maxRise = inLava ? 2.4 : 5
        const maxSink = inLava ? -1.8 : -4
        const riseAccel = inLava ? 10 : 18
        const sinkAccel = inLava ? 2.5 : 4

        if (this.spaceHolding) {
          this.velocity.y = Math.min(maxRise, this.velocity.y + riseAccel * delta)
        } else {
          this.velocity.y -= sinkAccel * delta
          if (this.camera.position.y < surfaceY + 0.35) {
            this.velocity.y += (inLava ? 4 : 8) * delta
          }
        }
        this.velocity.y = Math.max(maxSink, Math.min(maxRise, this.velocity.y))
      } else {
        // gravity
        if (Math.abs(this.velocity.y) < this.player.falling) {
          this.velocity.y -= 25 * delta
        }
      }

      // up collide handler
      if (this.upCollide) {
        this.velocity.y = -225 * delta
        this.far = this.player.body.height
      }

      // fall damage tracking
      if (this.player.gameMode === GameMode.survival) {
        if (this.velocity.y < -2 && !this.player.isFalling) {
          this.player.isFalling = true
          this.player.fallStartY = this.camera.position.y
        }
        if (this.downCollide && this.player.isFalling) {
          const fallDistance = this.player.fallStartY - this.camera.position.y
          this.player.isFalling = false
          if (fallDistance > 3) {
            const damage = Math.floor(fallDistance - 3)
            this.player.takeDamage(damage)
            this.player.depleteHunger(Math.ceil(damage / 2))
            this.onDamage?.()
          }
        }
      }

      // down collide and jump handler
      if (this.swimming) {
        if (this.downCollide && this.velocity.y < -1) {
          this.velocity.y = -1
        }
      } else if (this.downCollide && !this.isJumping) {
        this.velocity.y = 0
        if (!this.spaceHolding) this.homingAttackReady = true
      } else if (this.downCollide && this.isJumping) {
        this.isJumping = false
        if (!this.spaceHolding) this.homingAttackReady = true
      }

      // side collide handler
      let vector = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion
      )
      let direction = Math.atan2(vector.x, vector.z)
      if (
        this.frontCollide ||
        this.backCollide ||
        this.leftCollide ||
        this.rightCollide
      ) {
        // collide front (positive x)
        if (this.frontCollide) {
          // camera front
          if (direction < Math.PI && direction > 0 && this.velocity.x > 0) {
            if (
              (!this.leftCollide && direction > Math.PI / 2) ||
              (!this.rightCollide && direction < Math.PI / 2)
            ) {
              this.moveZ(Math.PI / 2 - direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera back
          if (direction < 0 && direction > -Math.PI && this.velocity.x < 0) {
            if (
              (!this.leftCollide && direction > -Math.PI / 2) ||
              (!this.rightCollide && direction < -Math.PI / 2)
            ) {
              this.moveZ(-Math.PI / 2 - direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera left
          if (
            direction < Math.PI / 2 &&
            direction > -Math.PI / 2 &&
            this.velocity.z < 0
          ) {
            if (
              (!this.rightCollide && direction < 0) ||
              (!this.leftCollide && direction > 0)
            ) {
              this.moveZ(-direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }

          // camera right
          if (
            (direction < -Math.PI / 2 || direction > Math.PI / 2) &&
            this.velocity.z > 0
          ) {
            if (!this.rightCollide && direction > 0) {
              this.moveZ(Math.PI - direction, delta)
            }
            if (!this.leftCollide && direction < 0) {
              this.moveZ(-Math.PI - direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }
        }

        // collide back (negative x)
        if (this.backCollide) {
          // camera front
          if (direction < 0 && direction > -Math.PI && this.velocity.x > 0) {
            if (
              (!this.leftCollide && direction < -Math.PI / 2) ||
              (!this.rightCollide && direction > -Math.PI / 2)
            ) {
              this.moveZ(Math.PI / 2 + direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera back
          if (direction < Math.PI && direction > 0 && this.velocity.x < 0) {
            if (
              (!this.leftCollide && direction < Math.PI / 2) ||
              (!this.rightCollide && direction > Math.PI / 2)
            ) {
              this.moveZ(direction - Math.PI / 2, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera left
          if (
            (direction < -Math.PI / 2 || direction > Math.PI / 2) &&
            this.velocity.z < 0
          ) {
            if (!this.leftCollide && direction > 0) {
              this.moveZ(-Math.PI + direction, delta)
            }
            if (!this.rightCollide && direction < 0) {
              this.moveZ(Math.PI + direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }

          // camera right
          if (
            direction < Math.PI / 2 &&
            direction > -Math.PI / 2 &&
            this.velocity.z > 0
          ) {
            if (
              (!this.leftCollide && direction < 0) ||
              (!this.rightCollide && direction > 0)
            ) {
              this.moveZ(direction, delta)
            }
          } else if (
            !this.leftCollide &&
            !this.rightCollide &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }
        }

        // collide left (negative z)
        if (this.leftCollide) {
          // camera front
          if (
            (direction < -Math.PI / 2 || direction > Math.PI / 2) &&
            this.velocity.x > 0
          ) {
            if (!this.frontCollide && direction > 0) {
              this.moveX(Math.PI - direction, delta)
            }
            if (!this.backCollide && direction < 0) {
              this.moveX(-Math.PI - direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.frontCollide &&
            direction < 0 &&
            direction > -Math.PI / 2 &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.backCollide &&
            direction < Math.PI / 2 &&
            direction > 0 &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera back
          if (
            direction < Math.PI / 2 &&
            direction > -Math.PI / 2 &&
            this.velocity.x < 0
          ) {
            if (
              (!this.frontCollide && direction < 0) ||
              (!this.backCollide && direction > 0)
            ) {
              this.moveX(-direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.frontCollide &&
            direction < Math.PI &&
            direction > Math.PI / 2 &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.backCollide &&
            direction > -Math.PI &&
            direction < -Math.PI / 2 &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera left
          if (direction > 0 && direction < Math.PI && this.velocity.z < 0) {
            if (
              (!this.backCollide && direction > Math.PI / 2) ||
              (!this.frontCollide && direction < Math.PI / 2)
            ) {
              this.moveX(Math.PI / 2 - direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.frontCollide &&
            direction > -Math.PI &&
            direction < -Math.PI / 2 &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.backCollide &&
            direction > -Math.PI / 2 &&
            direction < 0 &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }

          // camera right
          if (direction < 0 && direction > -Math.PI && this.velocity.z > 0) {
            if (
              (!this.backCollide && direction > -Math.PI / 2) ||
              (!this.frontCollide && direction < -Math.PI / 2)
            ) {
              this.moveX(-Math.PI / 2 - direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.frontCollide &&
            direction < Math.PI / 2 &&
            direction > 0 &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.backCollide &&
            direction < Math.PI &&
            direction > Math.PI / 2 &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }
        }

        // collide right (positive z)
        if (this.rightCollide) {
          // camera front
          if (
            direction < Math.PI / 2 &&
            direction > -Math.PI / 2 &&
            this.velocity.x > 0
          ) {
            if (
              (!this.backCollide && direction < 0) ||
              (!this.frontCollide && direction > 0)
            ) {
              this.moveX(direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.frontCollide &&
            direction < -Math.PI / 2 &&
            direction > -Math.PI &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.backCollide &&
            direction < Math.PI &&
            direction > Math.PI / 2 &&
            this.velocity.x > 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera back
          if (
            (direction < -Math.PI / 2 || direction > Math.PI / 2) &&
            this.velocity.x < 0
          ) {
            if (!this.backCollide && direction > 0) {
              this.moveX(-Math.PI + direction, delta)
            }
            if (!this.frontCollide && direction < 0) {
              this.moveX(Math.PI + direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.frontCollide &&
            direction < Math.PI / 2 &&
            direction > 0 &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          } else if (
            this.backCollide &&
            direction < 0 &&
            direction > -Math.PI / 2 &&
            this.velocity.x < 0
          ) {
            this.control.moveForward(this.velocity.x * delta)
          }

          // camera left
          if (direction < 0 && direction > -Math.PI && this.velocity.z < 0) {
            if (
              (!this.frontCollide && direction > -Math.PI / 2) ||
              (!this.backCollide && direction < -Math.PI / 2)
            ) {
              this.moveX(Math.PI / 2 + direction, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.frontCollide &&
            direction > Math.PI / 2 &&
            direction < Math.PI &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.backCollide &&
            direction > 0 &&
            direction < Math.PI / 2 &&
            this.velocity.z < 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }

          // camera right
          if (direction > 0 && direction < Math.PI && this.velocity.z > 0) {
            if (
              (!this.frontCollide && direction > Math.PI / 2) ||
              (!this.backCollide && direction < Math.PI / 2)
            ) {
              this.moveX(direction - Math.PI / 2, delta)
            }
          } else if (
            !this.frontCollide &&
            !this.backCollide &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.frontCollide &&
            direction > -Math.PI / 2 &&
            direction < 0 &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          } else if (
            this.backCollide &&
            direction > -Math.PI &&
            direction < -Math.PI / 2 &&
            this.velocity.z > 0
          ) {
            this.control.moveRight(this.velocity.z * delta)
          }
        }
      } else {
        // no collide
        this.control.moveForward(this.velocity.x * delta)
        this.control.moveRight(this.velocity.z * delta)
      }

      this.camera.position.y += this.velocity.y * delta

      // catching net / void damage
      if (this.camera.position.y < -100) {
        if (this.player.gameMode === GameMode.survival) {
          this.player.takeDamage(100)
        }
        this.player.isFalling = false
        this.camera.position.y = 60
      }
    }
    this.updateFluidDamage(delta, eyeInWater, inWater, inLava)

    if (this.player.mode === Mode.flying) {
      this.swimming = false
    }
    this.p2 = this.p1
  }
}
